/**
 * Screen sharing with the support, on every signed-in page of the app.
 *
 * - Presence: a small signal every 30 seconds (and on each page change) tells
 *   the support console that this person has Callizy open, and on which page.
 * - A support request arrives through the live channel (SSE), right away;
 *   the presence signal also returns it, as a fallback. A window asks:
 *   Accept or Decline, for 2 minutes.
 * - The client can offer a share from the support widget: a confirmation
 *   window, then a code to read over the phone while the support joins.
 * - While shared: a banner "Support can see your screen · Stop" stays on top.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { MonitorUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { useSSE } from '@/contexts/SSEContext'
import { screenShareApi, type PendingRequest } from '@/api/screenShare'
import { useAuthStore } from '@/stores/useAuthStore'
import { shareSession } from './session'
import { SCREEN_SHARE_NS } from './strings'

const PRESENCE_MS = 30_000

function useShareState() {
  return useSyncExternalStore((cb) => shareSession.subscribe(cb), () => shareSession.read())
}

function errorCode(err: unknown): string {
  const data = (err as { response?: { data?: { code?: unknown } } })?.response?.data
  return typeof data?.code === 'string' ? data.code : ''
}

export function ScreenShareGlobal() {
  const { t } = useTranslation(SCREEN_SHARE_NS)
  const location = useLocation()
  const signedIn = useAuthStore((s) => Boolean(s.accessToken))
  const { onScreenShare } = useSSE()
  const state = useShareState()
  const [request, setRequest] = useState<(PendingRequest & { until: number }) | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [busy, setBusy] = useState(false)
  const answered = useRef(new Set<number>())

  // After a reload: the share of this tab resumes (banner first).
  useEffect(() => { if (signedIn) void shareSession.resume() }, [signedIn])

  const showRequest = (r: PendingRequest) => {
    if (answered.current.has(r.id) || shareSession.read().phase !== 'idle') return
    setRequest({ ...r, until: Date.now() + r.expire_dans * 1000 })
  }

  // The live channel: the request, or its withdrawal.
  useEffect(() => onScreenShare((event) => {
    if (event.type === 'demande' && event.support !== undefined) {
      showRequest({ id: event.id, support: event.support, expire_dans: event.expire_dans ?? 120 })
    } else if (event.type === 'annulee') {
      setRequest((r) => (r && r.id === event.id ? null : r))
    }
  }), [onScreenShare])

  // Presence: every 30 seconds while the tab is visible, and on each page change.
  const page = `${location.pathname}${location.search}`
  useEffect(() => {
    if (!signedIn) return undefined
    let stopped = false
    const ping = () => {
      if (document.visibilityState !== 'visible') return
      screenShareApi.presence(page).then((r) => {
        if (!stopped && r.demande) showRequest(r.demande)
      }).catch(() => undefined)
    }
    ping()
    const timer = window.setInterval(ping, PRESENCE_MS)
    document.addEventListener('visibilitychange', ping)
    return () => {
      stopped = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', ping)
    }
  }, [signedIn, page])

  // The countdown of the request and of the code.
  const ticking = Boolean(request) || state.phase === 'code'
  useEffect(() => {
    if (!ticking) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [ticking])
  useEffect(() => {
    if (request && request.until <= now) setRequest(null)
  }, [request, now])

  // The end message, once.
  useEffect(() => {
    if (state.phase !== 'ended') return
    toast(t(`end.${state.end || 'erreur'}`))
    shareSession.forgetEnd()
  }, [state.phase, state.end, t])

  const answer = async (accept: boolean) => {
    if (!request) return
    const id = request.id
    answered.current.add(id)
    setRequest(null)
    try {
      if (accept) await shareSession.accept(id)
      else await screenShareApi.refuse(id)
    } catch (err) {
      const code = errorCode(err)
      toast.error(t(`errors.${['expire', 'deja_repondu', 'deja_en_cours'].includes(code) ? code : 'default'}`))
    }
  }

  const offer = async () => {
    setBusy(true)
    try {
      await shareSession.offer()
    } catch (err) {
      shareSession.cancelConfirmation()
      const code = errorCode(err)
      toast.error(t(`errors.${code === 'deja_en_cours' ? code : 'default'}`))
    } finally {
      setBusy(false)
    }
  }

  const secondsLeft = request ? Math.max(0, Math.round((request.until - now) / 1000)) : 0
  const codeMinutes = state.expiresAt ? Math.max(1, Math.ceil((state.expiresAt - now) / 60000)) : 10
  const code = state.code ? `${state.code.slice(0, 3)} ${state.code.slice(3)}` : ''

  return (
    <>
      {/* The support asks to see the screen. */}
      <Dialog open={Boolean(request)} onOpenChange={(open) => { if (!open) void answer(false) }}>
        {/* An explicit choice only: a click beside the window must not decline for the client. */}
        <DialogContent showCloseButton={false} className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t('request.title')}</DialogTitle>
            <DialogDescription>{t('request.message', { name: request?.support ?? '' })}</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">{t('request.expires', { seconds: secondsLeft })}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => void answer(false)}>{t('request.refuse')}</Button>
            <Button onClick={() => void answer(true)}>{t('request.accept')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The client offers to share, from the support widget. */}
      <Dialog open={state.phase === 'confirm'} onOpenChange={(open) => { if (!open && !busy) shareSession.cancelConfirmation() }}>
        <DialogContent showCloseButton={false} className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t('confirm.title')}</DialogTitle>
            <DialogDescription>{t('confirm.message')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => shareSession.cancelConfirmation()}>{t('confirm.cancel')}</Button>
            <Button disabled={busy} onClick={() => void offer()}>{t('confirm.share')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The code, while waiting for the support to join. Like the banner, usable over any window. */}
      {state.phase === 'code' && (
        <div data-over-dialogs=""
          className="pointer-events-auto fixed bottom-4 left-4 z-[2147483000] w-[300px] max-w-[calc(100vw-2rem)] rounded-xl border bg-background p-4 shadow-lg">
          <p className="text-sm font-medium">{t('code.title')}</p>
          <p className="mt-1 font-mono text-3xl font-semibold tracking-[0.2em]">{code}</p>
          <p className="mt-2 text-xs text-muted-foreground">{t('code.help', { minutes: codeMinutes })}</p>
          <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" aria-hidden />
            {t('code.waiting')}
          </p>
          <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => shareSession.stop()}>
            {t('code.cancel')}
          </Button>
        </div>
      )}

      {/* While shared: the banner stays on top, with a labelled Stop button. An open window turns off
          pointer events on the page and closes on a click outside: the banner takes them back
          (pointer-events-auto) and a click on it never closes the window (data-over-dialogs). */}
      {(state.phase === 'live' || state.phase === 'waiting') && (
        <div role="status" data-over-dialogs=""
          className="pointer-events-auto fixed left-1/2 top-2 z-[2147483000] flex max-w-[calc(100vw-1rem)] -translate-x-1/2 items-center gap-3 rounded-full bg-blue-600 py-1.5 pl-4 pr-1.5 text-sm text-white shadow-lg">
          <MonitorUp className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">
            {state.phase === 'waiting' ? t('banner.waiting') : state.supportAway ? t('banner.away') : t('banner.live')}
          </span>
          <button type="button" onClick={() => shareSession.stop()}
            className="shrink-0 rounded-full bg-white px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-50">
            {t('banner.stop')}
          </button>
        </div>
      )}
    </>
  )
}
